import { useEffect } from 'react';

import { Card, CardBody, Form, LabeledSwitch } from '@/_components';
import {
  BulkDeleteError,
  ContainerLayout,
  DataTable,
  DataTableToolbar,
  FiltersContextWithAccordion,
  FilterTrigger,
  InfiniteScrollStatus,
  NewButton,
  NewButtonUI,
  StickyPageHeader,
} from '@/_containers';
import { usePermissions } from '@/_providers';

import { InvoiceBottomPanels } from './invoice-bottom-panels';
import { InvoicesSplitView } from './invoices-split-view';

import { filterRenderer } from '../../main-invoices/components';
import { useMainInvoicesPage } from '../../main-invoices/use-main-invoices-page';
import { useFilterFormDefinition } from '../../shared/filters';
import { getInvoiceListRowClassName } from '../../shared/invoice-void-row-style';
import { useInvoiceDigitalPdfShortcut } from '../hooks/use-invoice-digital-pdf-shortcut';
import { useInvoiceEmailShortcut } from '../hooks/use-invoice-email-shortcut';
import type { PageShellState } from '../use-page';

type InvoicesViewProps = {
  page: PageShellState;
};

export function InvoicesView({ page }: InvoicesViewProps) {
  const {
    invoiceFilters,
    isEnter,
    enterState,
    activeInvoiceId,
    setActiveInvoiceId,
    isBatchMode,
    batchMode,
    handleKeyDown,
    handleInvoiceRowClick,
    handleInvoiceRowFocus,
    handleOpenArPayments,
    paymentCreation,
    openEmailModal,
    beginSendingInvoice,
    endSendingInvoice,
    sendingInvoiceId,
  } = page;
  const { currentModelPermissions } = usePermissions();
  const invoiceForm = useFilterFormDefinition();
  const mainInvoicesTable = useMainInvoicesPage(invoiceFilters, {
    openEmailModal,
    beginSendingInvoice,
    endSendingInvoice,
  });
  const hasInvoices = mainInvoicesTable.allInvoices.length > 0;

  useInvoiceDigitalPdfShortcut({
    activeInvoiceId,
    invoices: mainInvoicesTable.allInvoices,
  });

  useInvoiceEmailShortcut({
    activeInvoiceId,
    invoices: mainInvoicesTable.allInvoices,
    sendingInvoiceId,
    onOpenEmailModal: openEmailModal,
  });

  useEffect(() => {
    mainInvoicesTable.table.toggleAllRowsExpanded(Boolean(invoiceFilters.searching.search_str));
  }, [invoiceFilters.searching.search_str, mainInvoicesTable.table]);

  useEffect(() => {
    if (!isEnter) {
      return;
    }
    enterState.setFalse();
    mainInvoicesTable.handleOnSingleValueResult();
  }, [enterState, isEnter, mainInvoicesTable]);

  useEffect(() => {
    if (!activeInvoiceId) {
      return;
    }

    const isCurrentInvoiceInTable = mainInvoicesTable.allInvoices.some(
      (invoice) => String(invoice.id) === activeInvoiceId
    );

    if (!isCurrentInvoiceInTable) {
      setActiveInvoiceId(null);
    }
  }, [activeInvoiceId, mainInvoicesTable.allInvoices, setActiveInvoiceId]);

  return (
    <Form {...invoiceForm} isSubmitting={false}>
      <FiltersContextWithAccordion
        title="Invoices"
        advFilters={invoiceFilters.advFiltersState}
        handleSubmitAdvFilters={invoiceFilters.handleSubmitAdvFilters}
        handleClearAllAdvFilters={invoiceFilters.handleClearAllAdvFilters}
        filterRenderer={filterRenderer}
      >
        <ContainerLayout>
          <StickyPageHeader title="Invoices" recordCount={mainInvoicesTable.total}>
            {currentModelPermissions?.create && (
              <div className="flex items-center gap-2">
                {!!paymentCreation && <NewButtonUI onClick={handleOpenArPayments} title="AR Payment" osKey="P" />}
                <NewButton to="/invoices/new" title="New Invoice Batch" />
              </div>
            )}
          </StickyPageHeader>

          <BulkDeleteError hasIssues={mainInvoicesTable.hasIssues} name="Invoice" pluralName="Invoices" />

          <article className="mt-2 px-6">
            <Card>
              <CardBody className="space-y-4">
                <DataTableToolbar
                  table={mainInvoicesTable.table}
                  searching={invoiceFilters.searching}
                  isDeleting={mainInvoicesTable.isBulkDeleting}
                  deleteCallback={currentModelPermissions?.delete ? mainInvoicesTable.handleBulkDelete : undefined}
                  onKeyDown={handleKeyDown}
                  rightSlot={<FilterTrigger advFilters={invoiceFilters.advFiltersState} />}
                >
                  <div className="flex flex-1 justify-end gap-2">
                    <LabeledSwitch
                      id="show"
                      tabIndex={-1}
                      checked={isBatchMode}
                      onCheckedChange={batchMode.toggle}
                      label="Invoice Batches"
                      labelPlacement="before"
                    />
                  </div>
                </DataTableToolbar>

                {hasInvoices ? (
                  <InvoicesSplitView
                    topContent={
                      <DataTable
                        table={mainInvoicesTable.table}
                        tabIndex={2}
                        virtualized
                        infiniteScroll={{
                          fetchNextPage: mainInvoicesTable.fetchNextPage,
                          hasNextPage: mainInvoicesTable.hasNextPage,
                          isFetchingNextPage: mainInvoicesTable.isFetchingNextPage,
                        }}
                        onFocusEnter={mainInvoicesTable.handleFocusEnter}
                        onRowClick={handleInvoiceRowClick}
                        onRowFocus={handleInvoiceRowFocus}
                        isRowActive={(row) => String(row.id) === activeInvoiceId}
                        getRowClassName={(row) => getInvoiceListRowClassName(row.original, sendingInvoiceId)}
                        containerClassName="h-full overflow-auto rounded-b-none"
                        tableHeaderClassName="sticky top-0 z-10 bg-white dark:bg-gray-950"
                      />
                    }
                    bottomContent={
                      <InvoiceBottomPanels
                        activeInvoiceId={activeInvoiceId}
                        listInvoices={mainInvoicesTable.allInvoices}
                      />
                    }
                  />
                ) : (
                  <DataTable
                    table={mainInvoicesTable.table}
                    tabIndex={2}
                    virtualized
                    onFocusEnter={mainInvoicesTable.handleFocusEnter}
                    onRowClick={handleInvoiceRowClick}
                    onRowFocus={handleInvoiceRowFocus}
                    isRowActive={(row) => String(row.id) === activeInvoiceId}
                    getRowClassName={(row) => getInvoiceListRowClassName(row.original, sendingInvoiceId)}
                    tableHeaderClassName="sticky top-0 z-10 bg-white dark:bg-gray-950"
                  />
                )}

                <InfiniteScrollStatus
                  loadedCount={mainInvoicesTable.allInvoices.length}
                  total={mainInvoicesTable.total}
                  isFetchingNextPage={mainInvoicesTable.isFetchingNextPage}
                  hasNextPage={mainInvoicesTable.hasNextPage}
                />
              </CardBody>
            </Card>
          </article>
        </ContainerLayout>
      </FiltersContextWithAccordion>
    </Form>
  );
}
